import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  BarChart3, TrendingUp, DollarSign, Calendar, Users, Star,
  Heart, Clock, MapPin, Music, ArrowRight, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClientAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/client/analytics');
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 text-white/40" />
          <h2 className="text-2xl font-bold text-white mb-2">No Analytics Data</h2>
          <p className="text-white/60 mb-6">Book your first artist to see your analytics!</p>
          <Button onClick={() => navigate('/browse')} className="bg-violet-600 hover:bg-violet-500">
            Browse Artists
          </Button>
        </div>
      </div>
    );
  }

  // Process monthly spending data for chart
  const monthlyData = Object.entries(analytics.monthly_spending || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6);
  
  const maxSpending = Math.max(...monthlyData.map(([, data]) => data.amount), 1);

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short' });
  };

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4" data-testid="client-analytics-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <BarChart3 className="w-8 h-8 text-violet-400" />
          <h1 className="text-3xl font-bold text-white">Your Booking Insights</h1>
        </div>
        <p className="text-white/60 mb-8">Track your event history and discover your music preferences</p>

        {/* Top Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Spent */}
          <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-500/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-green-300/80 text-sm font-medium mb-1">Total Spent</p>
                  <p className="text-3xl font-bold text-white">${analytics.total_spent?.toLocaleString() || '0'}</p>
                  <p className="text-sm text-white/40 mt-1">All time</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Bookings */}
          <Card className="bg-gradient-to-br from-violet-500/20 to-purple-500/10 border-violet-500/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-violet-300/80 text-sm font-medium mb-1">Total Bookings</p>
                  <p className="text-3xl font-bold text-white">{analytics.total_bookings || 0}</p>
                  <p className="text-sm text-white/40 mt-1">{analytics.completed_bookings || 0} completed</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-violet-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Artists Booked */}
          <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/10 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-blue-300/80 text-sm font-medium mb-1">Artists Booked</p>
                  <p className="text-3xl font-bold text-white">{analytics.unique_artists || 0}</p>
                  <p className="text-sm text-white/40 mt-1">Unique artists</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Avg Booking Value */}
          <Card className="bg-gradient-to-br from-yellow-500/20 to-amber-500/10 border-yellow-500/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-yellow-300/80 text-sm font-medium mb-1">Avg Booking</p>
                  <p className="text-3xl font-bold text-white">${analytics.avg_booking_value || 0}</p>
                  <p className="text-sm text-white/40 mt-1">Per event</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Spending Chart */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                Spending Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {monthlyData.length === 0 ? (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 text-white/20" />
                  <p className="text-white/40">No spending data yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-end justify-between gap-2 h-40 px-2">
                    {monthlyData.map(([month, data]) => {
                      const height = (data.amount / maxSpending) * 100;
                      return (
                        <div key={month} className="flex-1 flex flex-col items-center group">
                          <div className="relative w-full flex justify-center">
                            <div 
                              className="w-8 bg-gradient-to-t from-green-600 to-green-400 rounded-t-lg transition-all duration-300 group-hover:from-green-500 group-hover:to-green-300 cursor-pointer"
                              style={{ 
                                height: `${Math.max(height, data.amount > 0 ? 8 : 0)}px`,
                                minHeight: data.amount > 0 ? '8px' : '0'
                              }}
                            />
                            <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                              ${data.amount.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between gap-2 px-2 border-t border-white/10 pt-3">
                    {monthlyData.map(([month]) => (
                      <div key={month} className="flex-1 text-center">
                        <span className="text-xs text-white/40">{formatMonth(month)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Favorite Genres */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Music className="w-5 h-5 text-violet-400" />
                Your Favorite Genres
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!analytics.top_genres || analytics.top_genres.length === 0 ? (
                <div className="text-center py-8">
                  <Music className="w-12 h-12 mx-auto mb-4 text-white/20" />
                  <p className="text-white/40">No genre data yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {analytics.top_genres.slice(0, 5).map((genre, index) => {
                    const maxCount = analytics.top_genres[0].count;
                    const width = (genre.count / maxCount) * 100;
                    const colors = [
                      'from-violet-500 to-purple-500',
                      'from-blue-500 to-cyan-500',
                      'from-green-500 to-emerald-500',
                      'from-yellow-500 to-amber-500',
                      'from-pink-500 to-rose-500'
                    ];
                    return (
                      <div key={genre.name} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-white">{genre.name}</span>
                          <span className="text-white/40">{genre.count} bookings</span>
                        </div>
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                          <div 
                            className={`h-full bg-gradient-to-r ${colors[index]} rounded-full transition-all duration-500`}
                            style={{ width: `${width}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Favorite Artists */}
        <Card className="bg-white/5 border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-400" />
              Your Favorite Artists
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!analytics.favorite_artists || analytics.favorite_artists.length === 0 ? (
              <div className="text-center py-8">
                <Heart className="w-12 h-12 mx-auto mb-4 text-white/20" />
                <p className="text-white/40">No favorite artists yet</p>
                <Button onClick={() => navigate('/browse')} variant="outline" className="mt-4">
                  Discover Artists
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {analytics.favorite_artists.slice(0, 6).map((artist, index) => (
                  <div 
                    key={artist.artist_id}
                    className="p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/artists/${artist.artist_id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                        {artist.image_url ? (
                          <img src={artist.image_url} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="text-white font-bold">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{artist.name || 'Artist'}</p>
                        <p className="text-white/40 text-sm">{artist.booking_count} bookings</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-violet-400 transition-colors" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Bookings */}
        <Card className="bg-white/5 border-white/10 mb-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-400" />
              Recent Bookings
            </CardTitle>
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-violet-400">
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {!analytics.recent_bookings || analytics.recent_bookings.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-white/20" />
                <p className="text-white/40">No recent bookings</p>
              </div>
            ) : (
              <div className="space-y-3">
                {analytics.recent_bookings.slice(0, 5).map((booking) => (
                  <div 
                    key={booking.booking_id}
                    className="p-4 bg-white/5 rounded-xl flex items-center justify-between hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-violet-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{booking.event_type || 'Event'}</p>
                        <div className="flex items-center gap-2 text-white/40 text-sm">
                          <span>{new Date(booking.event_date).toLocaleDateString()}</span>
                          {booking.venue && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {booking.venue}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-medium">${booking.total_cost}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        booking.booking_status === 'completed' 
                          ? 'bg-green-500/20 text-green-400'
                          : booking.booking_status === 'confirmed'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {booking.booking_status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card className="bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-violet-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Personalized Tips</h3>
                <ul className="space-y-2 text-white/60 text-sm">
                  {analytics.total_bookings < 5 && (
                    <li>• Book 5 or more artists to unlock loyalty discounts!</li>
                  )}
                  {analytics.avg_booking_value > 500 && (
                    <li>• Consider our Premium client membership for exclusive perks and priority booking</li>
                  )}
                  {analytics.unique_artists < 3 && (
                    <li>• Explore new artists! We have hundreds of talented performers waiting to make your event special</li>
                  )}
                  <li>• Leave reviews for your past bookings to help other clients find great artists</li>
                  <li>• Refer friends and earn $25 credit for each successful referral!</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
